import React from "react";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  SelectChangeEvent,
} from "@mui/material";
import { OrgRole, AccountStatus } from "@shared/schemas";

interface UserFiltersProps {
  search: string;
  role?: OrgRole;
  status?: AccountStatus;
  onSearchChange: (search: string) => void;
  onRoleChange: (role?: OrgRole) => void;
  onStatusChange: (status?: AccountStatus) => void;
  onResetFilters: () => void;
}

/**
 * Component for filtering the user list
 */
const UserFilters: React.FC<UserFiltersProps> = ({
  search,
  role,
  status,
  onSearchChange,
  onRoleChange,
  onStatusChange,
  onResetFilters,
}) => {
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.target.value);
  };

  // Handle role select change
  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onRoleChange(value === "" ? undefined : (value as OrgRole));
  };

  // Handle status select change
  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onStatusChange(value === "" ? undefined : (value as AccountStatus));
  };

  // Handle reset button click
  const handleReset = () => {
    onResetFilters();
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name or email"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              id="role-select"
              value={role || ""}
              label="Role"
              onChange={handleRoleChange}
            >
              <MenuItem value="">All Roles</MenuItem>
              <MenuItem value="OWNER">Owner</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
              <MenuItem value="MEMBER">Member</MenuItem>
              <MenuItem value="VIEWER">Viewer</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={status || ""}
              label="Status"
              onChange={handleStatusChange}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="CREATED">Created</MenuItem>
              <MenuItem value="UNVERIFIED">Unverified</MenuItem>
              <MenuItem value="ONBOARDING">Onboarding</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="SUSPENDED">Suspended</MenuItem>
              <MenuItem value="LOCKED">Locked</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button variant="outlined" onClick={handleReset} fullWidth>
            Reset
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserFilters;
